/**
 * The staged decorrelation stretch.
 *
 * This module defines the *definition* of the transform as an explicit,
 * readable sequence of steps. `core/compose.ts` collapses the same sequence
 * into one affine matrix; `core/apply.ts` runs both. The property test in
 * `compose.test.ts` asserts the two agree.
 *
 * # The stages
 *
 * Given an input pixel `x` (RGB, 0..255):
 *
 *   1. centre     `u = x - mu`
 *   2. rotate     `y = E^T u`        (E's columns are the eigenvectors of C)
 *   3. stretch    `z = D y`          (D = diag(gain / sqrt(max(lambda_i, floor))))
 *   4. bias       `z' = z + c`       (c is `bandOffset`, in stretched space)
 *   5. recolour   `w = R z'`         (R is `recolor`, default identity)
 *   6. recentre   `out = w + t`      (t depends on `recolorMode`)
 *   7. clamp      `out = clamp(out, 0, 255)` if `clamp`
 *
 * Step 7 is the only nonlinearity, which is what makes step 1-6 collapse into a
 * single 3x3 matrix plus an offset (hypothesis T2).
 *
 * # Numerical hazards
 *
 * A near-zero eigenvalue `lambda_3` means the least-variance band is mostly
 * noise. `D_33 = gain / sqrt(lambda_3)` would then amplify that noise without
 * limit. Two guards apply, in this order:
 *
 *   - `lambda_i` is floored at `lambdaFloor` (default `1e-6`),
 *   - `D_ii` is capped at `dMax` (default 255).
 *
 * This trade-off *is* the DStretch look. Removing the guards produces a
 * colourful but meaningless image.
 *
 * @module core/stretch
 */

import type { Mat3, StretchParams, Vec3 } from './types.ts';
import { diag, identity, mul, mulVec } from './mat3.ts';
import { jacobiEigen } from './jacobi.ts';

/** Default eigenvalue floor. */
export const LAMBDA_FLOOR = 1e-6;
/** Default cap on a stretch factor. */
export const D_MAX = 255;

/** The stretched-space scale factors, `D_ii`. Exposed for tests and reports. */
export function stretchFactors(
  values: ArrayLike<number>,
  gain = 1,
  lambdaFloor = LAMBDA_FLOOR,
  dMax = D_MAX,
): Vec3 {
  const d = new Float64Array(3);
  for (let i = 0; i < 3; i++) {
    const lambda = Math.max(values[i]!, lambdaFloor);
    d[i] = Math.min(gain / Math.sqrt(lambda), dMax);
  }
  return d;
}

/** The recentring translation `t` for a given mode. */
export function recolorTranslation(mode: StretchParams['recolorMode'], mean: Vec3): Vec3 {
  switch (mode ?? 'mean') {
    case 'zero':
      return new Float64Array(3);
    case 'mid':
      return Float64Array.of(127.5, 127.5, 127.5);
    case 'mean':
    default:
      return Float64Array.from(mean);
  }
}

/** The rotation `E^T`, so that `mulVec(rotation, x)` gives principal coordinates. */
export function rotationFrom(params: StretchParams): { rotation: Mat3; factors: Vec3; eigenvalues: Vec3 } {
  const eigen = jacobiEigen(params.covariance);
  return {
    rotation: transposeInPlace(eigen.vectors),
    factors: stretchFactors(eigen.values, params.gain ?? 1, params.lambdaFloor, params.dMax),
    eigenvalues: eigen.values,
  };
}

/** Local transpose (kept here to avoid a name clash with the imported `mul`). */
function transposeInPlace(m: Mat3): Mat3 {
  const o = new Float64Array(9);
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) o[c * 3 + r] = m[r * 3 + c]!;
  return o;
}

/** The full staged transform, as a list of steps. Used by the property test. */
export interface StagedSteps {
  mean: Vec3;
  rotation: Mat3;
  factors: Vec3;
  recolor: Mat3;
  bandOffset: Vec3;
  translation: Vec3;
  outputOffset: Vec3;
  clamp: boolean;
}

/** Expand `StretchParams` into its explicit stages. */
export function stagedSteps(params: StretchParams): StagedSteps {
  const { rotation, factors } = rotationFrom(params);
  return {
    mean: Float64Array.from(params.mean),
    rotation,
    factors,
    recolor: params.recolor ? Float64Array.from(params.recolor) : identity(),
    bandOffset: params.bandOffset ? Float64Array.from(params.bandOffset) : new Float64Array(3),
    translation: recolorTranslation(params.recolorMode, params.mean),
    outputOffset: params.outputOffset ? Float64Array.from(params.outputOffset) : new Float64Array(3),
    clamp: params.clamp ?? true,
  };
}

/**
 * Run the staged pipeline on one pixel, **without clamping**.
 * Clamping is applied by the caller so the property test can compare the
 * pre-clamp values of both paths.
 */
export function applyStagedPreClamp(steps: StagedSteps, x: ArrayLike<number>, out?: Vec3): Vec3 {
  // 1. centre
  const u = Float64Array.of(x[0]! - steps.mean[0]!, x[1]! - steps.mean[1]!, x[2]! - steps.mean[2]!);
  // 2. rotate into principal coordinates
  const y = mulVec(steps.rotation, u);
  // 3. stretch
  const z = Float64Array.of(
    steps.factors[0]! * y[0]!,
    steps.factors[1]! * y[1]!,
    steps.factors[2]! * y[2]!,
  );
  // 4. per-band bias
  z[0] = z[0]! + steps.bandOffset[0]!;
  z[1] = z[1]! + steps.bandOffset[1]!;
  z[2] = z[2]! + steps.bandOffset[2]!;
  // 5. recolour
  const w = mulVec(steps.recolor, z);
  // 6. recentre
  const o = out ?? new Float64Array(3);
  o[0] = w[0]! + steps.translation[0]! + steps.outputOffset[0]!;
  o[1] = w[1]! + steps.translation[1]! + steps.outputOffset[1]!;
  o[2] = w[2]! + steps.translation[2]! + steps.outputOffset[2]!;
  return o;
}

/** Run the staged pipeline on one pixel, with clamping. */
export function applyStaged(steps: StagedSteps, x: ArrayLike<number>, out?: Vec3): Vec3 {
  const o = applyStagedPreClamp(steps, x, out);
  if (steps.clamp) {
    o[0] = clamp255(o[0]!);
    o[1] = clamp255(o[1]!);
    o[2] = clamp255(o[2]!);
  }
  return o;
}

/** Clamp to the 0..255 output range. */
export function clamp255(v: number): number {
  // NaN must not propagate into the output; treat it as black.
  if (!(v > 0)) return 0;
  return v > 255 ? 255 : v;
}

/** `diag(D)` as a matrix, for callers that want the stretch as a matrix. */
export function factorMatrix(factors: ArrayLike<number>): Mat3 {
  return diag(factors[0]!, factors[1]!, factors[2]!);
}

/** The staged matrix `R * D * E^T`, before recentring is folded in. */
export function stagedMatrix(steps: StagedSteps): Mat3 {
  return mul(mul(steps.recolor, factorMatrix(steps.factors)), steps.rotation);
}

/** Convenience: the eigenvalues of the covariance, descending. */
export function eigenvaluesOf(params: StretchParams): Vec3 {
  return jacobiEigen(params.covariance).values;
}
